import { execSync } from 'node:child_process';
import ora from 'ora';
import { extractRepoName } from './git-clone.js';
import { DependencyUsageAnalyzer } from '../analysis/dependency-usage-analyzer.js';
import { RepoUrlResolver } from '../analysis/repo-url-resolver.js';
import { createLogger } from '../logging/index.js';
import { createServices, destroyServices } from '../services/index.js';
import type { PackageUsage } from '../analysis/dependency-usage-analyzer.js';

const logger = createLogger('plugin-commands');

/** Safely extract a string field from an unknown parsed JSON value. */
function extractStringField(obj: unknown, key: string): string | undefined {
  if (typeof obj !== 'object' || obj === null || !(key in obj)) return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(obj, key);
  if (descriptor === undefined) return undefined;
  const value: unknown = descriptor.value;
  return typeof value === 'string' ? value : undefined;
}

/**
 * Options passed from CLI global options to plugin command handlers.
 */
export interface CommandOptions {
  config?: string | undefined;
  dataDir?: string | undefined;
  projectRoot?: string | undefined;
  format?: 'json' | 'table' | 'plain' | undefined;
  quiet?: boolean | undefined;
}

export async function handleSearch(args: {
  query: string;
  stores?: string;
  limit?: string;
}): Promise<void> {
  // PWD is set by Claude Code to user's project directory
  const services = await createServices(undefined, undefined, process.env['PWD']);

  try {
    const storeNames = args.stores?.split(',').map((s: string) => s.trim());

    const allStores = await services.store.list();
    const targetStores =
      storeNames !== undefined ? allStores.filter((s) => storeNames.includes(s.name)) : allStores;

    if (targetStores.length === 0) {
      console.error('No stores found to search');
      process.exitCode = 1;
      return;
    }

    // Initialize stores
    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    for (const store of targetStores) {
      await services.lance.initialize(store.id);
    }

    const limitValue = args.limit ?? '10';
    const limit = parseInt(limitValue, 10);
    if (Number.isNaN(limit)) {
      throw new Error(`Invalid value for limit: "${limitValue}" is not a valid integer`);
    }

    const results = await services.search.search({
      query: args.query,
      stores: targetStores.map((s) => s.id),
      mode: 'hybrid',
      limit,
      detail: 'contextual',
    });

    console.log(`Found ${String(results.totalResults)} results:\n`);
    for (const r of results.results) {
      if (r.summary !== undefined) {
        console.log(`Score: ${r.score.toFixed(2)} - ${r.summary.location}`);
        console.log(r.summary.purpose);
      }
      console.log('---');
    }
  } finally {
    await destroyServices(services);
  }
}

export async function handleAddRepo(
  args: {
    url: string;
    name?: string;
    branch?: string;
  },
  options: CommandOptions = {}
): Promise<void> {
  const services = await createServices(
    options.config,
    options.dataDir,
    options.projectRoot ?? process.env['PWD']
  );

  try {
    const storeName = args.name ?? extractRepoName(args.url);

    console.log(`Cloning ${args.url}...`);

    const result = await services.store.create({
      name: storeName,
      type: 'repo',
      url: args.url,
      ...(args.branch !== undefined ? { branch: args.branch } : {}),
    });

    if (!result.success) {
      console.error(`Error: ${result.error.message}`);
      process.exitCode = 1;
      return;
    }

    console.log(`Created store: ${storeName} (${result.data.id})`);
    if ('path' in result.data) {
      console.log(`Location: ${result.data.path}`);
    }

    // Auto-index
    console.log('\nIndexing...');
    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    await services.lance.initialize(result.data.id);
    const indexResult = await services.index.indexStore(result.data);

    if (indexResult.success) {
      console.log(`Indexed ${String(indexResult.data.filesIndexed)} files`);
    } else {
      console.error(`Indexing failed: ${indexResult.error.message}`);
    }
  } finally {
    await destroyServices(services);
  }
}

export async function handleAddFolder(
  args: { path: string; name?: string },
  options: CommandOptions = {}
): Promise<void> {
  const services = await createServices(
    options.config,
    options.dataDir,
    options.projectRoot ?? process.env['PWD']
  );

  try {
    const { basename } = await import('node:path');
    const storeName = args.name ?? basename(args.path);

    console.log(`Adding folder: ${args.path}...`);

    const result = await services.store.create({
      name: storeName,
      type: 'file',
      path: args.path,
    });

    if (!result.success) {
      console.error(`Error: ${result.error.message}`);
      process.exitCode = 1;
      return;
    }

    console.log(`Created store: ${storeName} (${result.data.id})`);
    if ('path' in result.data) {
      console.log(`Location: ${result.data.path}`);
    }

    // Auto-index
    console.log('\nIndexing...');
    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    await services.lance.initialize(result.data.id);
    const indexResult = await services.index.indexStore(result.data);

    if (indexResult.success) {
      console.log(`Indexed ${String(indexResult.data.filesIndexed)} files`);
    } else {
      console.error(`Indexing failed: ${indexResult.error.message}`);
    }
  } finally {
    await destroyServices(services);
  }
}

export async function handleIndex(args: { store: string }): Promise<void> {
  // PWD is set by Claude Code to user's project directory
  const services = await createServices(undefined, undefined, process.env['PWD']);

  try {
    const store = await services.store.getByIdOrName(args.store);

    if (store === undefined) {
      console.error(`Store not found: ${args.store}`);
      process.exitCode = 1;
      return;
    }

    console.log(`Indexing ${store.name}...`);
    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    await services.lance.initialize(store.id);
    const result = await services.index.indexStore(store);

    if (result.success) {
      console.log(
        `Indexed ${String(result.data.filesIndexed)} documents in ${String(result.data.timeMs)}ms`
      );
    } else {
      console.error(`Error: ${result.error.message}`);
      process.exitCode = 1;
      return;
    }
  } finally {
    await destroyServices(services);
  }
}

export async function handleStores(options: CommandOptions = {}): Promise<void> {
  const services = await createServices(
    options.config,
    options.dataDir,
    options.projectRoot ?? process.env['PWD']
  );

  try {
    const stores = await services.store.list();

    if (stores.length === 0) {
      console.log('No stores found.');
      console.log('\nCreate a store with:');
      console.log('  /bluera-knowledge:add-repo <url> --name=<name>');
      console.log('  /bluera-knowledge:add-folder <path> --name=<name>');
      return;
    }

    // Table header
    console.log('| Name | Type | ID | Source |');
    console.log('|------|------|----|--------------------|');

    // Table rows
    for (const store of stores) {
      const name = store.name;
      const type = store.type;
      const id = store.id;
      let source = '';

      if ('url' in store && store.url !== undefined) {
        source = store.url;
      } else if ('path' in store) {
        source = store.path;
      }

      console.log(`| ${name} | ${type} | ${id.substring(0, 8)}... | ${source} |`);
    }
  } finally {
    await destroyServices(services);
  }
}

/**
 * Use Claude CLI to select the most important dependencies for BK indexing.
 * Falls back to top-N by usage frequency if Claude CLI is unavailable.
 */
function filterImportantDeps(usages: PackageUsage[]): PackageUsage[] {
  if (usages.length <= 5) return usages;

  const depList = usages
    .map(
      (u) =>
        `- ${u.packageName}: ${String(u.importCount)} imports in ${String(u.fileCount)} files${u.isDevDependency ? ' (dev)' : ''}`
    )
    .join('\n');

  const prompt = [
    "Given this project's dependencies ranked by import frequency:",
    '',
    depList,
    '',
    'Select the dependencies that would be MOST VALUABLE to have their source code indexed for an AI coding assistant. Focus on:',
    '- Core frameworks and libraries the project builds on (not dev tools)',
    '- Libraries with complex APIs where understanding internals helps',
    '- Libraries likely to cause debugging issues or have non-obvious behavior',
    '- Skip: test runners, linters, formatters, build tools, @types packages, trivial utilities',
    '',
    'Return ONLY a JSON array of package names, e.g.: ["react", "prisma", "zod"]',
    'Do not explain. Just the JSON array.',
  ].join('\n');

  const output = execSync('claude -p --output-format json --model haiku', {
    input: prompt,
    encoding: 'utf-8',
    timeout: 30000,
    stdio: ['pipe', 'pipe', 'pipe'],
  });

  const parsed: unknown = JSON.parse(output);
  const resultText = extractStringField(parsed, 'result');
  if (resultText === undefined) {
    throw new Error('Unexpected Claude CLI output format');
  }

  // Extract JSON array from response (Claude might wrap it in markdown code blocks)
  const jsonMatch = /\[[\s\S]*\]/.exec(resultText);
  if (jsonMatch === null) {
    throw new Error('No JSON array found in Claude response');
  }

  const selected: unknown = JSON.parse(jsonMatch[0]);
  if (!Array.isArray(selected)) {
    throw new Error('Claude response is not an array');
  }

  const selectedNames = new Set(selected.filter((s): s is string => typeof s === 'string'));
  const filtered = usages.filter((u) => selectedNames.has(u.packageName));

  // If Claude filtered too aggressively (< 2 results), fall back
  if (filtered.length < 2) {
    throw new Error('Claude returned too few results');
  }

  return filtered;
}

export async function handleSuggest(options: CommandOptions = {}): Promise<void> {
  const projectRoot = options.projectRoot ?? process.env['PWD'] ?? process.cwd();

  console.log('Analyzing project dependencies...\n');

  // Create analyzer instance
  const services = await createServices(options.config, options.dataDir, projectRoot);

  try {
    const analyzer = new DependencyUsageAnalyzer();
    const resolver = new RepoUrlResolver();

    // Analyze with progress indicator
    const spinner = ora('Scanning source files...').start();

    const result = await analyzer.analyze(projectRoot, (current, total, message) => {
      spinner.text = `${message} (${String(current)}/${String(total)})`;
    });

    spinner.stop();

    if (!result.success) {
      console.error(`Error: ${result.error.message}`);
      process.exitCode = 1;
      return;
    }

    const { usages, totalFilesScanned, skippedFiles } = result.data;

    console.log(
      `✔ Scanned ${String(totalFilesScanned)} files${skippedFiles > 0 ? ` (skipped ${String(skippedFiles)})` : ''}\n`
    );

    if (usages.length === 0) {
      console.log('No external dependencies found in this project.');
      console.log('\nMake sure you have a package.json or requirements.txt file.');
      return;
    }

    // Filter out packages already in stores
    const existingStores = await services.store.list();
    const existingRepoNames = new Set(existingStores.map((s) => s.name));

    const newUsages = usages.filter((u) => !existingRepoNames.has(u.packageName));

    if (newUsages.length === 0) {
      console.log('✔ All dependencies are already in knowledge stores!');
      return;
    }

    // Use AI to select the most important dependencies for BK indexing
    let topSuggestions: PackageUsage[];
    try {
      const spinner2 = ora('Selecting important dependencies with AI...').start();
      topSuggestions = filterImportantDeps(newUsages);
      spinner2.stop();
      console.log('AI-selected dependencies most valuable for source indexing:\n');
    } catch (error) {
      logger.debug(
        { error: error instanceof Error ? error.message : String(error) },
        'AI filtering unavailable, falling back to top-5 by usage'
      );
      topSuggestions = newUsages.slice(0, 5);
      console.log('Top dependencies by usage in this project:\n');
    }
    topSuggestions.forEach((usage, i) => {
      console.log(`${String(i + 1)}. ${usage.packageName}`);
      console.log(
        `   ${String(usage.importCount)} imports across ${String(usage.fileCount)} files\n`
      );
    });

    console.log('Searching for repository URLs...\n');

    // For each package, find repo URL
    for (const usage of topSuggestions) {
      const repoResult = await resolver.findRepoUrl(usage.packageName, usage.language);

      if (repoResult.url !== null) {
        console.log(`✔ ${usage.packageName}: ${repoResult.url}`);
        console.log(`  /bluera-knowledge:add-repo ${repoResult.url} --name=${usage.packageName}\n`);
      } else {
        console.log(`✗ ${usage.packageName}: Could not find repository URL`);
        console.log(
          `  You can manually add it: /bluera-knowledge:add-repo <url> --name=${usage.packageName}\n`
        );
      }
    }

    console.log('Use the commands above to add repositories to your knowledge stores.');
  } finally {
    await destroyServices(services);
  }
}
